import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Save, GripVertical, Layers } from "lucide-react";
import { DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { WIDGET_TYPES, POSITIONS, VISIBILITY, newWidget } from "../../lib/WidgetConstants";

export default function IDGWidgetsTab() {
  const [widgets, setWidgets] = useState([]);

  const load = () => api.get("/idg/widgets").then((r) => setWidgets(r.data));
  useEffect(() => { load(); }, []);

  const persistOrder = async (next) => {
    await api.post("/idg/widgets/reorder", next.map((w, i) => ({ id: w.id, position: w.position, order: i })));
  };

  const groupedBy = (pos) => widgets.filter((w) => w.position === pos).sort((a, b) => (a.order || 0) - (b.order || 0));

  const updateWidget = async (w) => {
    try { await api.patch(`/idg/widgets/${w.id}`, w); toast.success("Salvat"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const create = async (position) => {
    try { await api.post("/idg/widgets", newWidget(position)); toast.success("Widget adăugat"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Sigur ștergi widget-ul?")) return;
    await api.delete(`/idg/widgets/${id}`);
    load();
  };

  return (
    <div className="space-y-3" data-testid="idg-widgets-tab">
      <div className="flex items-center gap-2">
        <Layers className="w-4 h-4 text-[var(--tf-primary)]" />
        <h3 className="tf-section-title">Widget-uri Sidebar — drag & drop</h3>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {POSITIONS.map((p) => (
          <PositionColumn
            key={p.value}
            position={p}
            widgets={groupedBy(p.value)}
            onCreate={() => create(p.value)}
            onUpdate={updateWidget}
            onRemove={remove}
            onReorder={(reordered) => {
              const updated = widgets.map((w) => {
                if (w.position !== p.value) return w;
                const idx = reordered.findIndex((x) => x.id === w.id);
                return { ...w, order: idx };
              });
              setWidgets(updated);
              persistOrder(reordered);
            }}
          />
        ))}
      </div>
    </div>
  );
}

function PositionColumn({ position, widgets, onCreate, onUpdate, onRemove, onReorder }) {
  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));

  const onDragEnd = (e) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIdx = widgets.findIndex((x) => x.id === active.id);
    const newIdx = widgets.findIndex((x) => x.id === over.id);
    onReorder(arrayMove(widgets, oldIdx, newIdx));
  };

  return (
    <div className="tf-surface tf-clip-card p-3 space-y-2" data-testid={`widget-position-${position.value}`}>
      <div className="flex items-center justify-between border-b border-white/5 pb-2">
        <h4 className="tf-section-title">{position.label}</h4>
        <button onClick={onCreate} className="tf-btn-secondary tf-clip-btn text-[10px] flex items-center gap-1" data-testid={`widget-add-${position.value}`}>
          <Plus className="w-3 h-3" /> Adaugă
        </button>
      </div>
      {widgets.length === 0 && <p className="text-xs tf-mono text-zinc-500 py-3 text-center">Nimic aici</p>}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={widgets.map((w) => w.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-2 min-h-12">
            {widgets.map((w, idx) => <WidgetCard key={w.id} widget={w} idx={idx} onUpdate={onUpdate} onRemove={() => onRemove(w.id)} />)}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
}

function WidgetCard({ widget, idx, onUpdate, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: widget.id });
  const [local, setLocal] = useState(widget);
  useEffect(() => setLocal(widget), [widget]);
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  const set = (k, v) => setLocal({ ...local, [k]: v });
  const setSetting = (k, v) => setLocal({ ...local, settings: { ...(local.settings || {}), [k]: v } });

  return (
    <div ref={setNodeRef} style={style} className="tf-surface-elev tf-clip-card-sm border border-white/10 p-2 space-y-1.5" data-testid={`widget-card-${widget.id}`}>
      <div className="flex items-center gap-2">
        <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing text-zinc-500 hover:text-[var(--tf-primary)]">
          <GripVertical className="w-3.5 h-3.5" />
        </button>
        <select className="tf-input py-1 text-[11px] flex-1" value={local.type} onChange={(e) => set("type", e.target.value)} data-testid={`widget-type-${widget.id}`}>
          {WIDGET_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <label className="flex items-center gap-1 text-[10px]">
          <input type="checkbox" checked={!!local.enabled} onChange={(e) => set("enabled", e.target.checked)} />
          <span className="text-zinc-400">on</span>
        </label>
        <button onClick={onRemove} className="text-red-500 hover:bg-red-500/10 p-0.5" data-testid={`widget-remove-${widget.id}`}>
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
      <input className="tf-input py-1 text-[11px]" placeholder="Titlu afișat (opțional)" value={local.title || ""} onChange={(e) => set("title", e.target.value)} />
      {local.type === "html" ? (
        <textarea className="tf-input font-mono text-[10px] py-1" rows={3} placeholder="<div>...</div>" value={local.settings?.custom_html || ""} onChange={(e) => setSetting("custom_html", e.target.value)} />
      ) : (
        <div className="grid grid-cols-2 gap-1.5">
          <input type="number" className="tf-input py-1 text-[10px]" placeholder="Nr items" value={local.settings?.items_count || 5} onChange={(e) => setSetting("items_count", parseInt(e.target.value) || 5)} />
          <select className="tf-input py-1 text-[10px]" value={local.settings?.visibility || "all"} onChange={(e) => setSetting("visibility", e.target.value)}>
            {VISIBILITY.map((v) => <option key={v.value} value={v.value}>{v.label}</option>)}
          </select>
          <label className="flex items-center gap-1 text-[10px] text-zinc-400">
            <input type="checkbox" checked={!!local.settings?.show_date} onChange={(e) => setSetting("show_date", e.target.checked)} />
            data
          </label>
          <label className="flex items-center gap-1 text-[10px] text-zinc-400">
            <input type="checkbox" checked={!!local.settings?.show_author} onChange={(e) => setSetting("show_author", e.target.checked)} />
            autor
          </label>
        </div>
      )}
      <button onClick={() => onUpdate(local)} className="w-full tf-btn-primary tf-clip-btn text-[10px] flex items-center justify-center gap-1" data-testid={`widget-save-${widget.id}`}>
        <Save className="w-3 h-3" /> Salvează
      </button>
    </div>
  );
}
